import { dist2, idiv, imax, isqrt, withinRange } from '../math/fixed.ts';
import type { BattleState, Enemy, Hero, SendaNpc } from '../types.ts';
import { enemiesNear, targetable } from '../battle/combat.ts';
import { orderMove } from '../battle/heroes.ts';
import { castSkill } from '../battle/skills.ts';
import { litterOrder } from '../battle/litter.ts';
import { ERM_LAMP, ermNoHeal } from '../battle/ermita.ts';
import { VEL_BRAZIER, velInBeam, velLampReady } from '../battle/vigilia.ts';

/**
 * The cleric's chapter 5 played by the lab's bot (docs/sendas/clerigo.md, «La vigilia»; ai/senda-bot-cleric.ts hands it
 * the fight): one that knows the act's rule and one that doesn't. Both heal (they're clerics). The mirrors again are
 * chapter 4's nave (ai/senda-bot-ermita.ts).
 *
 * - The lamps: who knows lights each lamp on its minute and lights again the ones put out first, and kills what is at a
 *   lit lamp (the limpets stuck on it before the dead); who doesn't lights them once, in order, and fights what comes at it.
 * - The atrium: who knows keeps by the nave's door, kills the bone singers first (they raise the rest), then the
 *   mourners, then what is at the door, and gets Joran and the apprentices up; who doesn't fights the nearest, wherever.
 * - The Doliente: who knows hushes her wail with holy blows, cuts the tongues (the one at the candle first), peels the
 *   limpets off Tessa, the door's people and the way's mirrors, thaws the frosted mirror, gets the fallen up, and from the
 *   sun stands so that she comes into it; who doesn't strikes her and what comes at it, and leaves the tongues be.
 *
 * It only moves the hero, picks its target and casts, as a player's commands would.
 */

const THINK = 30;

const go = (s: BattleState, h: Hero, x: number, y: number, slack = 300): void => {
  if (!h.moving && withinRange(h.x, h.y, x, y, 250)) return;
  if (!h.moving || Math.abs(h.destX - x) + Math.abs(h.destY - y) > slack) orderMove(s, h, x, y);
};

const nearestTo = (list: Enemy[], x: number, y: number): Enemy | null => {
  let best: Enemy | null = null;
  let bd = 0x7fffffff;
  for (const e of list) {
    const d = dist2(x, y, e.x, e.y);
    if (d < bd) {
      bd = d;
      best = e;
    }
  }
  return best;
};

const strike = (s: BattleState, h: Hero, t: Enemy, close = false): void => {
  h.target = t.id;
  h.forced = true;
  const reach = close ? imax(600, h.stats.range - 1600) : h.stats.range - 200;
  if (!withinRange(h.x, h.y, t.x, t.y, reach)) go(s, h, t.x, t.y, 300);
};

const person = (s: BattleState, id: string): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === id);
const alive = (s: BattleState, id: number): Enemy | undefined => s.enemies.find((e) => e.id === id && e.hp > 0);

/** Its heals: a mend on itself (not in a wail), the sanctuary on Tessa when beside her, dawn when low. */
const heals = (s: BattleState, h: Hero): void => {
  if (ermNoHeal(s)) return;
  const r = s.rules?.senda?.litter;
  const t = person(s, 'tessa');
  // (Against the Doliente it mends itself early, between wails, whether Tessa is hurt or not: a player does the same in
  // both branches of chapter 4's decision.)
  const meLow = h.hp * 100 < h.stats.maxHp * (s.rules?.senda?.litter?.hermit?.vela?.mourn ? 85 : 60);
  const beside = !!r && !!t && t.hp > 0 && withinRange(h.x, h.y, t.x, t.y, r.mendReach - 300);
  if (h.skillCd[0] === 0 && (meLow || (beside && t && t.hp <= t.maxHp - 4))) castSkill(s, h, { t: 'skill', slot: 0, id: h.id });
  if (h.skillCd[1] === 0 && t && beside && (t.hp * 2 <= t.maxHp || h.hp * 2 < h.stats.maxHp)) castSkill(s, h, { t: 'skill', slot: 1, x: t.x, y: t.y });
  if (h.skillCd[2] === 0 && h.hp * 10 < h.stats.maxHp * 3) castSkill(s, h, { t: 'skill', slot: 2 });
};

const BUSY = new WeakMap<BattleState, { wait: number; busy: boolean }>();
/** Between one piece of work and the next, a moment to look (as a player would). */
const ready = (s: BattleState): boolean => {
  const w = s.senda?.litter;
  let m = BUSY.get(s);
  if (!m) {
    m = { wait: s.tick + THINK, busy: false };
    BUSY.set(s, m);
  }
  if (w && w.job >= 0) {
    m.busy = true;
    return false;
  }
  if (m.busy) {
    m.busy = false;
    m.wait = s.tick + THINK;
  }
  return s.tick >= m.wait;
};

/** A fallen one of the path's people the hero can get up (not Tessa). */
const fallen = (s: BattleState, ids: string[]): SendaNpc | undefined => (s.senda?.npcs ?? []).find((n) => ids.includes(n.id) && n.hp <= 0 && !n.away);

const lift = (s: BattleState, h: Hero, n: SendaNpc): void => {
  if (!withinRange(h.x, h.y, n.x, n.y, 1100)) go(s, h, n.x + 300, n.y + 200, 200);
  else if (h.moving) orderMove(s, h, h.x, h.y);
};

/* ---------- act 1: the lamps ---------- */

const lampsBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const w = s.senda?.litter;
  const V = s.rules?.senda?.litter?.hermit?.vela;
  const v = w?.erm?.vel;
  if (!w || !V || !v) return;
  heals(s, h);
  const mine = w.limpets.map((l) => alive(s, l.id)).find((e): e is Enemy => !!e);
  if (mine) return strike(s, h, mine);
  if (knows) {
    // A lamp out: lit again before anything (two out lose).
    const out = v.lamp.findIndex((x) => x === 2);
    if (out >= 0 && w.job < 0) {
      litterOrder(s, h, ERM_LAMP + out);
      return;
    }
    // What is at a lit lamp: the limpets stuck on one first, then the dead by one.
    const stuck = v.onLamp.filter((x) => x.at >= 0).map((x) => alive(s, x.id)).find((e): e is Enemy => !!e);
    if (stuck && w.job < 0) return strike(s, h, stuck);
    const at = V.lamps.flatMap((p, i) => (v.lamp[i] === 1 ? enemiesNear(s, p.x, p.y, 1600, (e) => targetable(s, e) && e.kind !== 'gargoyle') : []));
    if (at.length && w.job < 0) return strike(s, h, nearestTo(at, h.x, h.y) as Enemy);
  }
  if (w.job >= 0) return;
  // The next lamp on its minute (who doesn't: once each, in order).
  const next = v.lamp.findIndex((x, i) => x === 0 && velLampReady(V, i, s.waveT));
  if (next >= 0 && ready(s)) {
    litterOrder(s, h, ERM_LAMP + next);
    return;
  }
  const foes = enemiesNear(s, h.x, h.y, 9000, (e) => targetable(s, e));
  const t = nearestTo(foes, h.x, h.y);
  if (t) return strike(s, h, t);
  h.target = 0;
  // Waiting by the next lamp to come.
  const k = v.lamp.findIndex((x) => x !== 1);
  const p = V.stands[k >= 0 ? k : V.stands.length - 1];
  if (p) go(s, h, p.x, p.y + 300, 500);
};

/* ---------- act 2: the atrium ---------- */

const procBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const V = s.rules?.senda?.litter?.hermit?.vela;
  const P = V?.procession;
  if (!V || !P) return;
  heals(s, h);
  const foes = enemiesNear(s, h.x, h.y, 20000, (e) => targetable(s, e));
  if (knows) {
    const down = fallen(s, ['joran', 'apprenticeA', 'apprenticeB']);
    const near = foes.filter((e) => withinRange(e.x, e.y, h.x, h.y, 1300)).length;
    if (down && near <= 1) return lift(s, h, down);
    // The bone singers first, the mourners next, then what is at the door.
    const singer = nearestTo(
      foes.filter((e) => e.kind === 'bonesinger'),
      h.x,
      h.y,
    );
    if (singer) return strike(s, h, singer);
    const mourner = nearestTo(
      foes.filter((e) => e.kind === 'mourner'),
      h.x,
      h.y,
    );
    if (mourner) return strike(s, h, mourner);
    const door = nearestTo(
      foes.filter((e) => withinRange(e.x, e.y, P.door.x, P.door.y, 3000)),
      P.door.x,
      P.door.y,
    );
    if (door) return strike(s, h, door);
    const t = nearestTo(foes, P.door.x, P.door.y);
    if (t && withinRange(t.x, t.y, P.door.x, P.door.y, 7000)) return strike(s, h, t);
    h.target = 0;
    go(s, h, P.door.x, P.door.y + 2200, 500);
    return;
  }
  const t = nearestTo(foes, h.x, h.y);
  if (t) return strike(s, h, t);
  h.target = 0;
};

/* ---------- act 4: the Doliente ---------- */

/** From the sun on: where to stand so that she, coming between the hero and the candle, floats into the beam. */
const lure = (s: BattleState): { x: number; y: number } | null => {
  const m = s.senda?.litter?.erm;
  const V = s.rules?.senda?.litter?.hermit?.vela;
  if (!m || !V) return null;
  let best: { x: number; y: number } | null = null;
  let bd = 0x7fffffff;
  const c = V.candle;
  for (let i = 0; i + 3 < m.segs.length; i += 4) {
    if ((m.weak[i / 4] ?? 0) >= 2) continue;
    const mx = idiv((m.segs[i] as number) + (m.segs[i + 2] as number), 2);
    const my = idiv((m.segs[i + 1] as number) + (m.segs[i + 3] as number), 2);
    const d = imax(1, isqrt(dist2(mx, my, c.x, c.y)));
    if (d < 2500) continue;
    // Behind that point from the candle, a little more than she keeps off the hero.
    const p = { x: mx + idiv((mx - c.x) * 1500, d), y: my + idiv((my - c.y) * 1500, d) };
    const dd = dist2(p.x, p.y, c.x, c.y);
    if (dd < bd) {
      bd = dd;
      best = p;
    }
  }
  return best;
};

const mournBot = (s: BattleState, h: Hero, knows: boolean): void => {
  const w = s.senda?.litter;
  const m = w?.erm;
  const V = s.rules?.senda?.litter?.hermit?.vela;
  const M = V?.mourn;
  const v = m?.vel;
  if (!w || !m || !V || !M || !v) return;
  heals(s, h);
  const boss = alive(s, m.boss);
  const mine = w.limpets.filter((l) => l.on === 2).map((l) => alive(s, l.id)).find((e): e is Enemy => !!e);
  if (mine) return strike(s, h, mine);
  if (!knows) {
    // Who doesn't: her, and what comes at it.
    const near = nearestTo(
      enemiesNear(s, h.x, h.y, 1600, (e) => targetable(s, e) && e.id !== m.boss && !v.tongues.some((t) => t.id === e.id)),
      h.x,
      h.y,
    );
    if (near) return strike(s, h, near);
    if (boss) return strike(s, h, boss);
    return;
  }
  // A tongue at the candle, or near it: cut first.
  const tongues = v.tongues
    .map((t) => ({ t, e: alive(s, t.id) }))
    .filter((x): x is { t: (typeof v.tongues)[number]; e: Enemy } => !!x.e)
    .sort((a, b) => (a.t.at >= 0 ? 0 : 1) - (b.t.at >= 0 ? 0 : 1) || dist2(a.e.x, a.e.y, V.candle.x, V.candle.y) - dist2(b.e.x, b.e.y, V.candle.x, V.candle.y));
  const tg = tongues[0];
  if (tg && (tg.t.at >= 0 || withinRange(tg.e.x, tg.e.y, V.candle.x, V.candle.y, 5200))) {
    w.job = -1;
    return strike(s, h, tg.e);
  }
  // Her wail: hushed with holy blows.
  if (boss && ermNoHeal(s) && h.hp * 10 > h.stats.maxHp * 3) {
    w.job = -1;
    return strike(s, h, boss);
  }
  // The frosted mirror: the brazier.
  if (v.frost >= 0 && w.job < 0 && ready(s)) {
    litterOrder(s, h, VEL_BRAZIER);
    return;
  }
  if (w.job >= 0) return;
  // Limpets: on Tessa, on the way's mirrors, on the door's people (peeled: they leap at the hero).
  const onTessa = w.limpets.filter((l) => l.on === 1).map((l) => alive(s, l.id)).find((e): e is Enemy => !!e);
  if (onTessa) return strike(s, h, onTessa);
  const cover = m.onMirror.filter((x) => (m.covered >> x.m) & 1).map((x) => alive(s, x.id)).find((e): e is Enemy => !!e);
  if (cover) return strike(s, h, cover);
  const onP = v.onPeople.filter((x) => x.on === 1).map((x) => alive(s, x.id)).find((e): e is Enemy => !!e);
  if (onP) return strike(s, h, onP);
  // The fallen up (Joran first: he holds the tongues).
  const down = fallen(s, ['joran', 'apprenticeA', 'apprenticeB', 'arriero', 'arrieroB']) ?? fallen(s, ['piet', 'ludo']);
  if (down && !enemiesNear(s, h.x, h.y, 1300, (e) => targetable(s, e)).length) return lift(s, h, down);
  // From the sun: stand where she comes into it, and strike her.
  if (boss && v.phase === 2 && v.sun === 1) {
    const p = lure(s);
    h.target = boss.id;
    h.forced = true;
    if (p && !velInBeam(m, boss.x, boss.y, 650)) go(s, h, p.x, p.y, 400);
    return;
  }
  // What is on the way to the candle or at the door: the dead in by the atrium.
  const dead = nearestTo(
    enemiesNear(s, h.x, h.y, 4000, (e) => targetable(s, e) && e.id !== m.boss && e.kind !== 'dragger'),
    h.x,
    h.y,
  );
  if (dead) return strike(s, h, dead);
  if (boss) {
    // Between her and the candle, in reach of her.
    strike(s, h, boss);
    return;
  }
};

/** The cleric's chapter 5 (true: it was one of its fights). */
export const vigiliaBot = (s: BattleState, h: Hero, knows: boolean): boolean => {
  const k = s.rules?.senda?.litter?.hermit?.vela?.kind;
  if (k === 'lamps') lampsBot(s, h, knows);
  else if (k === 'procession') procBot(s, h, knows);
  else if (k === 'mourn') mournBot(s, h, knows);
  else return false;
  return true;
};
